/**
 * Paycheck (Budget tab) — monthly budget planning guide.
 *
 * Shows income, savings, and cash flow as a planning reference.
 * Bill payment tracking lives in the Tracker tab.
 * Day-to-day cash reality lives in the Bank tab.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  fc,
  f0,
  mk,
  isNowMo,
  moIncome,
  moPaid,
  moVar,
  moSavings,
  getAllBills,
  getVarBudget,
  getAllCats,
  MOS,
  getCashFlow,
  sourcesToMonthly,
} from '../utils';
import { IncomeSources } from './paycheck/IncomeSources';
import { MonthlyBudgetCard } from './paycheck/MonthlyBudgetCard';
import { SavingsSection } from './paycheck/SavingsSection';

export default function Paycheck({ state, updateState, showToast, triggerConfetti, triggerPayday }) {
  const today = new Date();
  const [viewY, setViewY] = useState(today.getFullYear());
  const [viewM, setViewM] = useState(today.getMonth());
  const [logPCOpen, setLogPCOpen] = useState(false);
  const [pcDate, setPcDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [undoPaycheck, setUndoPaycheck] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null); // { id, amt, date }
  const [bankPrompt, setBankPrompt] = useState(null); // { amt } — offer to add to bank balance
  const undoPcTimerRef = useRef(null);
  const pcAmtRef = useRef();

  useEffect(() => () => { if (undoPcTimerRef.current) clearTimeout(undoPcTimerRef.current); }, []);

  const {
    cards, fixedBills, subscriptions, paidBills, incomeLog,
    varSpend, savDeposits, savContrib, budgetSlider, catBudgets,
    customCats, disabledCats, monthlyIncome, extraGoals,
  } = state;

  const savingsBuckets = extraGoals || [];
  const cats = getAllCats(customCats, disabledCats);
  const k = mk(viewY, viewM);
  const bills = getAllBills(cards, fixedBills, subscriptions);
  const vd = varSpend[k] || {};
  const incomeSources = state.incomeSources || [];
  const srcTotalMonthly = sourcesToMonthly(incomeSources);

  const prevMo = () => { if (viewM === 0) { setViewY((y) => y - 1); setViewM(11); } else setViewM((m) => m - 1); };
  const nextMo = () => { if (viewM === 11) { setViewY((y) => y + 1); setViewM(0); } else setViewM((m) => m + 1); };

  // ── Income ─────────────────────────────────────────────────────────────────
  const moLoggedInc = moIncome(incomeLog, k);
  const isFutureMo = viewY > today.getFullYear() || (viewY === today.getFullYear() && viewM > today.getMonth());
  const plannedInc = incomeSources.length > 0 ? srcTotalMonthly : isFutureMo ? 0 : monthlyIncome || 0;
  const inc = moLoggedInc > 0 ? moLoggedInc : plannedInc;

  // ── Monthly totals ──────────────────────────────────────────────────────────
  const paidTot = moPaid(paidBills, k, cards, fixedBills, subscriptions);
  const varTot = moVar(varSpend, k);
  const savDeps = savDeposits?.[k] || [];
  const savA = moSavings(savDeposits, k, savContrib);
  const { budget: varBudget } = getVarBudget(inc, bills, savA, budgetSlider ?? 50);
  const rem = inc - paidTot - varTot - savA;

  // ── Income log handlers ──────────────────────────────────────────────────────
  const savePaycheckEntry = (amt, dateStr, sourceId = null) => {
    if (!amt || amt <= 0) { showToast('Enter an amount'); return false; }
    const entry = {
      id: 'inc_' + Date.now(),
      amt,
      date: dateStr,
      ...(sourceId ? { sourceId } : {}),
    };
    const updated = [...(incomeLog || []), entry].sort((a, b) => a.date.localeCompare(b.date));
    updateState({ incomeLog: updated });
    return true;
  };

  const logPaycheck = () => {
    const amt = parseFloat(pcAmtRef.current?.value) || 0;
    if (!savePaycheckEntry(amt, pcDate)) return;
    if (pcAmtRef.current) pcAmtRef.current.value = '';
    setLogPCOpen(false);
    // Trigger coin animation for payday!
    triggerPayday?.();
    // Only prompt to update bank balance when logging income for the current month
    const isCurrentMonth = isNowMo(viewY, viewM);
    if (isCurrentMonth && (state.bankBalance || 0) > 0) {
      setBankPrompt({ amt });
    } else {
      showToast('💰 Income logged!');
    }
  };

  const deletePaycheck = (id) => {
    const entry = (incomeLog || []).find((e) => e.id === id);
    updateState({ incomeLog: (incomeLog || []).filter((e) => e.id !== id) });
    if (entry) {
      if (undoPcTimerRef.current) clearTimeout(undoPcTimerRef.current);
      setUndoPaycheck({ entry });
      undoPcTimerRef.current = setTimeout(() => setUndoPaycheck(null), 5000);
    }
  };

  const saveEditEntry = () => {
    if (!editingEntry) return;
    const amt = parseFloat(editingEntry.amt);
    if (!amt || amt <= 0) { showToast('Enter a valid amount'); return; }
    const updated = (incomeLog || []).map((e) =>
      e.id === editingEntry.id ? { ...e, amt, date: editingEntry.date } : e
    ).sort((a, b) => a.date.localeCompare(b.date));
    updateState({ incomeLog: updated });
    setEditingEntry(null);
    showToast('Updated!');
  };

  const handleUndoPaycheck = () => {
    if (!undoPaycheck) return;
    clearTimeout(undoPcTimerRef.current);
    const { entry } = undoPaycheck;
    setUndoPaycheck(null);
    const updated = [...(incomeLog || []), entry].sort((a, b) => a.date.localeCompare(b.date));
    updateState({ incomeLog: updated });
    showToast('Restored!');
  };

  // Logged entries for the viewed month
  const monthEntries = (incomeLog || []).filter((e) => {
    if (!e.date) return false;
    const parts = e.date.split('-').map(Number);
    return parts[0] === viewY && parts[1] - 1 === viewM;
  });

  return (
    <div>
      {/* Monthly budget snapshot — spending categories at a glance */}
      <MonthlyBudgetCard
        cats={cats}
        catBudgets={catBudgets}
        vd={vd}
        varBudget={varBudget}
        varTot={varTot}
        budgetSlider={budgetSlider}
      />

      {/* Income Sources — what you earn / monthly reference */}
      <IncomeSources
        incomeSources={incomeSources}
        srcTotalMonthly={srcTotalMonthly}
        updateState={updateState}
        showToast={showToast}
      />

      {/* Month navigator + income log */}
      <div className="card cp" style={{ marginBottom: 12 }}>
        <div className="mhn">
          <button className="mna" onClick={prevMo}>&#8249;</button>
          <div>
            <div className="mlb">{MOS[viewM]} {viewY}</div>
            <div className="mls">
              {isNowMo(viewY, viewM) ? 'Current month' : ''}
            </div>
          </div>
          <button className="mna" onClick={nextMo}>&#8250;</button>
        </div>

        {/* Income received this month */}
        <div style={{ marginTop: 10, borderTop: '1px solid var(--bdr)', paddingTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
            💰 Income Received
            {moLoggedInc > 0 && <span style={{ color: 'var(--grn)', marginLeft: 6, fontWeight: 400 }}>{fc(moLoggedInc)}</span>}
          </div>

          {monthEntries.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Group by source */}
              {incomeSources.map((src) => {
                const srcEntries = monthEntries.filter((e) => e.sourceId === src.id);
                if (!srcEntries.length) return null;
                const srcTotal = srcEntries.reduce((s, e) => s + e.amt, 0);
                return (
                  <div key={src.id} style={{ padding: '8px 10px', background: 'var(--surf2)', borderRadius: 8, border: '1px solid var(--bdr)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: srcEntries.length > 1 ? 6 : 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>{src.name}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--grn)' }}>{fc(srcTotal)}</span>
                    </div>
                    {srcEntries.map((e) => (
                      <div key={e.id} style={{ fontSize: 11, padding: '2px 0', color: 'var(--mut)' }}>
                        {editingEntry?.id === e.id ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                            <input type="date" value={editingEntry.date} onChange={(ev) => setEditingEntry((x) => ({ ...x, date: ev.target.value }))} style={{ padding: '3px 6px', borderRadius: 5, border: '1px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 11 }} />
                            <input type="number" min="0" step="0.01" value={editingEntry.amt} onChange={(ev) => setEditingEntry((x) => ({ ...x, amt: ev.target.value }))} style={{ width: 80, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 11 }} autoFocus />
                            <button onClick={saveEditEntry} style={{ padding: '3px 8px', borderRadius: 5, background: 'var(--ylw)', color: '#000', fontWeight: 700, fontSize: 11, border: 'none', cursor: 'pointer' }}>Save</button>
                            <button onClick={() => setEditingEntry(null)} style={{ padding: '3px 7px', borderRadius: 5, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ color: 'var(--grn)', fontWeight: 600 }}>{fc(e.amt)}</span>
                              <button className="del" onClick={() => setEditingEntry({ id: e.id, amt: String(e.amt), date: e.date })}>✏️</button>
                              <button className="del" onClick={() => deletePaycheck(e.id)}>✕</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              {/* One-time / no-source entries */}
              {monthEntries.filter((e) => !e.sourceId).map((e) => (
                <div key={e.id} style={{ padding: '8px 10px', background: 'var(--surf2)', borderRadius: 8, border: '1px solid var(--bdr)', fontSize: 12 }}>
                  {editingEntry?.id === e.id ? (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input type="date" value={editingEntry.date} onChange={(ev) => setEditingEntry((x) => ({ ...x, date: ev.target.value }))} style={{ padding: '3px 6px', borderRadius: 5, border: '1px solid var(--bdr)', background: 'var(--bg)', color: 'var(--txt)', fontSize: 11 }} />
                      <input type="number" min="0" step="0.01" value={editingEntry.amt} onChange={(ev) => setEditingEntry((x) => ({ ...x, amt: ev.target.value }))} style={{ width: 80, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--bdr)', background: 'var(--bg)', color: 'var(--txt)', fontSize: 11 }} autoFocus />
                      <button onClick={saveEditEntry} style={{ padding: '3px 8px', borderRadius: 5, background: 'var(--ylw)', color: '#000', fontWeight: 700, fontSize: 11, border: 'none', cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditingEntry(null)} style={{ padding: '3px 7px', borderRadius: 5, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--mut)' }}>
                        {new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · other income
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontWeight: 700, color: 'var(--grn)' }}>{fc(e.amt)}</span>
                        <button className="del" onClick={() => setEditingEntry({ id: e.id, amt: String(e.amt), date: e.date })}>✏️</button>
                        <button className="del" onClick={() => deletePaycheck(e.id)}>✕</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {incomeSources.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--mut)', padding: '4px 2px' }}>
                  <span>Expected this month</span>
                  <span>{fc(srcTotalMonthly)}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="em" style={{ marginBottom: 8 }}>
              {incomeSources.length > 0 ? 'No income logged yet this month' : 'No income logged yet'}
            </div>
          )}

          {/* Bank balance prompt — offer to add logged income to bank balance */}
          {bankPrompt && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, padding: '8px 12px', marginTop: 6, background: 'rgba(107,203,119,.08)', border: '1px solid rgba(107,203,119,.3)', borderRadius: 8, fontSize: 12 }}>
              <span style={{ color: 'var(--txt)', fontWeight: 600 }}>💰 Add {fc(bankPrompt.amt)} to your bank balance?</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => {
                    updateState({ bankBalance: Math.round(((state.bankBalance || 0) + bankPrompt.amt) * 100) / 100 });
                    setBankPrompt(null);
                    showToast('Bank balance updated!');
                  }}
                  style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 7, border: 'none', background: 'var(--grn)', color: '#000', cursor: 'pointer' }}
                >
                  + Add
                </button>
                <button
                  onClick={() => { setBankPrompt(null); showToast('Income logged!'); }}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 7, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          {/* Undo banner */}
          {undoPaycheck && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', marginTop: 6, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', borderRadius: 8, fontSize: 12 }}>
              <span style={{ color: 'var(--mut)' }}>Deleted <strong>{fc(undoPaycheck.entry.amt)}</strong></span>
              <button onClick={handleUndoPaycheck} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 7, border: '1.5px solid var(--ylw)', background: 'rgba(255,217,61,.12)', color: 'var(--ylw)', cursor: 'pointer' }}>Undo</button>
            </div>
          )}

          {/* Log income form */}
          {logPCOpen ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surf2)', border: '2px solid var(--ylw)', borderRadius: 9, padding: '6px 10px', flex: 1 }}>
                  <span style={{ fontSize: 13, color: 'var(--mut)', fontWeight: 700 }}>$</span>
                  <input
                    ref={pcAmtRef}
                    type="number" min="0" step="0.01" placeholder="Amount"
                    style={{ flex: 1, background: 'none', border: 'none', fontSize: 15, fontWeight: 700, color: 'var(--ylw)', fontFamily: 'inherit' }}
                    onKeyDown={(e) => e.key === 'Enter' && logPaycheck()}
                    autoFocus
                  />
                </div>
                <input
                  type="date" value={pcDate}
                  onChange={(e) => setPcDate(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: 9, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={logPaycheck} style={{ flex: 1, padding: '8px', borderRadius: 9, background: 'var(--ylw)', color: '#000', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer' }}>+ Log</button>
                <button onClick={() => setLogPCOpen(false)} style={{ padding: '8px 10px', borderRadius: 9, background: 'var(--surf2)', color: 'var(--mut)', fontSize: 12, border: '1px solid var(--bdr)', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => {
                const d = isNowMo(viewY, viewM) ? new Date() : new Date(viewY, viewM, 1);
                setPcDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                setLogPCOpen(true);
              }}
              style={{ width: '100%', padding: '7px', borderRadius: 9, border: '1px dashed var(--bdr)', background: 'none', color: 'var(--mut)', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}
            >
              + Log income
            </button>
          )}
        </div>
      </div>

      {/* Savings */}
      <SavingsSection
        activeSavDeps={savDeps}
        savingsBuckets={savingsBuckets}
        activeSavTot={savDeps.reduce((s, d) => s + d.amt, 0)}
        savContrib={savContrib}
        savDeposits={savDeposits}
        k={k}
        extraGoals={extraGoals}
        updateState={updateState}
        showToast={showToast}
        triggerConfetti={triggerConfetti}
      />

      {/* Monthly Cash Flow */}
      {(() => {
        const cf = getCashFlow(state);
        if (cf.inc === 0) return null;
        const highAPR = (cards || []).filter((c) => c.bal > 0).sort((a, b) => b.apr - a.apr)[0];
        const nearGoal = (savingsBuckets || []).filter((g) => g.target > 0 && g.bal < g.target).sort((a, b) => b.bal / b.target - a.bal / a.target)[0];
        let tip = null;
        if (cf.leftOver > 0 && highAPR && highAPR.apr > 10)
          tip = { msg: `Put extra toward ${highAPR.name} (${highAPR.apr.toFixed(1)}% APR) to cut interest costs`, c: 'var(--grn)' };
        else if (cf.leftOver > 0 && nearGoal) {
          const pct = Math.round((nearGoal.bal / nearGoal.target) * 100);
          tip = { msg: `${pct}% toward ${nearGoal.name} — ${f0(Math.max(0, nearGoal.target - nearGoal.bal))} to go`, c: 'var(--ylw)' };
        } else if (cf.leftOver < 0)
          tip = { msg: `Spending exceeds income by ${f0(Math.abs(cf.leftOver))} — review budgets`, c: 'var(--red)' };
        return (
          <div className="card cp" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
              📊 Monthly Cash Flow
            </div>
            {[
              { l: '💰 Income', v: cf.inc, c: 'var(--grn)', bold: true },
              { l: '📋 Fixed Bills', v: -cf.billsTotal, c: 'var(--red)' },
              { l: '🛒 Variable Budget', v: -cf.varTotal, c: 'var(--pnk)' },
              { l: '💵 Savings', v: -cf.savTotal, c: 'var(--blu)' },
            ].map((row) => (
              <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--bdr)', fontSize: row.bold ? 14 : 13 }}>
                <span style={{ fontWeight: row.bold ? 700 : 500 }}>{row.l}</span>
                <span style={{ fontWeight: 700, color: row.c }}>{row.v >= 0 ? f0(row.v) : '−' + f0(Math.abs(row.v))}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 16 }}>
              <span style={{ fontWeight: 700 }}>Left Over</span>
              <span style={{ fontWeight: 800, color: cf.leftOver >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                {cf.leftOver >= 0 ? f0(cf.leftOver) : '−' + f0(Math.abs(cf.leftOver))}
              </span>
            </div>
            {tip && (
              <div style={{ padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 500, color: tip.c, background: tip.c === 'var(--red)' ? 'rgba(255,107,107,.08)' : 'rgba(107,203,119,.08)', border: `1px solid ${tip.c === 'var(--red)' ? 'rgba(255,107,107,.2)' : tip.c === 'var(--ylw)' ? 'rgba(255,217,61,.2)' : 'rgba(107,203,119,.2)'}` }}>
                {tip.c === 'var(--red)' ? '⚠️' : '💡'} {tip.msg}
              </div>
            )}
          </div>
        );
      })()}

      {/* Past-month summary bar */}
      {inc > 0 && !isNowMo(viewY, viewM) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '8px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 10, marginBottom: 12, fontSize: 11, color: 'var(--mut)' }}>
          <span>💰 {fc(inc)} earned</span>
          <span>💳 {fc(paidTot + varTot)} spent</span>
          <span>💵 {fc(savA)} saved</span>
          <span style={{ color: rem >= 0 ? 'var(--grn)' : 'var(--red)', fontWeight: 700 }}>
            {rem >= 0 ? fc(rem) + ' under' : fc(Math.abs(rem)) + ' over'} budget
          </span>
        </div>
      )}

      {/* Monthly overview */}
      {inc > 0 && (
        <div className="card cp" style={{ marginBottom: 12 }}>
          <div className="st" style={{ marginBottom: 10 }}>💰 {MOS[viewM]} Overview</div>
          <div className="bk">
            {[
              { l: 'Income', v: inc, c: 'var(--ylw)' },
              { l: 'Bills Paid', v: paidTot, c: 'var(--red)' },
              { l: 'Variable', v: varTot, c: 'var(--pnk)' },
              { l: 'Savings', v: savA, c: 'var(--grn)' },
              { l: 'Remaining', v: Math.abs(rem), c: rem >= 0 ? 'var(--grn)' : 'var(--red)' },
            ].map((t) => (
              <div key={t.l} className="bi2" style={{ borderLeftColor: t.c }}>
                <div className="bil">{t.l}</div>
                <div className="biv" style={{ color: t.c }}>{fc(t.v)}</div>
              </div>
            ))}
          </div>
          {/* Stacked spending bar */}
          {inc > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ height: 8, borderRadius: 99, background: 'var(--bdr)', overflow: 'hidden', display: 'flex' }}>
                {[
                  { v: paidTot, c: 'var(--red)' },
                  { v: bills.reduce((s, b) => s + b.amt, 0) - paidTot, c: 'rgba(255,107,107,.2)' },
                  { v: varTot, c: 'var(--pnk)' },
                  { v: savA, c: 'var(--grn)' },
                  { v: rem > 0 ? rem : 0, c: 'rgba(107,203,119,.2)' },
                ].map((seg, i) => (
                  <div key={i} style={{ width: Math.min(100, Math.max(0, (seg.v / inc) * 100)) + '%', background: seg.c, transition: 'width .5s' }} />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--mut)', marginTop: 3 }}>
                <span>{fc(paidTot + varTot + savA)} used</span>
                <span>{fc(inc)} total</span>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
